import { compact, intersection } from '@quromedical/utils';
import type { Group, Patient, Practitioner } from 'fhir/r4';
import { createReference } from '../mapping';
import type { WithId } from '../types';
import { filterReferencesByType } from '../utils';

/**
 * This function checks if a patient is assigned to a practitioner.
 */
export const isPatientAssignedToPractitioner = (
  practitioner: WithId<Practitioner>,
  patient: Patient,
): boolean => {
  const practitionerRef = createReference('Practitioner', practitioner.id);

  const { generalPractitioner = [] } = patient;

  const allowedPractitionerRefs = filterReferencesByType(
    'Practitioner',
    generalPractitioner,
  ).map((ref) => ref.reference);

  const directAccessRef = allowedPractitionerRefs.find(
    (ref) => ref === practitionerRef.reference,
  );

  return !!directAccessRef;
};

/**
 * This function checks if a patient is assigned to a care-team(group) that a practitioner is a member of.
 * If a group's organization is the same as the patient's care team organization, then
 * the patient is indirectly assigned to the practitioner via the group.
 */
export const isPatientAssignedToPractitionerGroup = (
  activeMemberGroups: Group[],
  patient: Patient,
): boolean => {
  const groupOrgRefs = compact(
    activeMemberGroups.map((group) => group.managingEntity?.reference),
  );

  const allowedOrgRefs = filterReferencesByType(
    'Organization',
    patient.generalPractitioner,
  ).map((ref) => ref.reference);

  const commonOrgs = intersection(compact(allowedOrgRefs), groupOrgRefs);

  const hasCommonOrgs = commonOrgs.length > 0;

  return hasCommonOrgs;
};

/**
 * Check if a practitioner has access to a patient by either:
 * 1. Being in the `generalPractitioner` list
 * 2. Having groups which are managed by an Organization that is in the `generalPractitioner` list
 */
export const isPatientAssignedToPractitionerOrGroups = (
  patient: Patient,
  practitioner: WithId<Practitioner>,
  activeMemberGroups: Group[],
): boolean => {
  if (!practitioner.id) {
    return false;
  }

  const directAccess = isPatientAssignedToPractitioner(practitioner, patient);
  const indirectAccess = isPatientAssignedToPractitionerGroup(
    activeMemberGroups,
    patient,
  );

  return directAccess || indirectAccess;
};
